import { OnInit, Component, inject } from '@angular/core';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  StandCycleRotationCreateDto,
  StandCycleRotationUpdateDto,
} from '../proxy/geographic/dtos/stand-cycle-rotation';
import { StandCycleRotationService } from '../proxy/geographic/controllers';
import { STANDCYCLEROTATION_CONFIG, STANDCYCLEROTATION_FIELDS } from './stand-cycle-rotation.config';
import { BaseCrudComponent, CrudPage, GenericForm, IActionsSchema, resolveActions, ICrudService, normalizePropertyPrefilterQuery } from '@zendra/shared';
import { StandCycleRotationOrchestratorService } from 'orchestrator';
import {
  GetStandCycleRotationProjectionListInputDto,
  StandCycleRotationProjectionDto,
  StandCycleRotationProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/stand-cycle-rotations/models';

@Component({
  selector: 'app-stand-cycle-rotation',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
  ],
  providers: [
    ListService,
    StandCycleRotationService,
    ConfirmationService,
  ],
  templateUrl: './stand-cycle-rotation.html',
  styleUrl: './stand-cycle-rotation.scss',
})
export class StandCycleRotation extends BaseCrudComponent<
  StandCycleRotationProjectionDto,
  StandCycleRotationCreateDto,
  StandCycleRotationUpdateDto,
  StandCycleRotationProjectionExcelDownloadDto
> implements OnInit {
  geographicService = inject(StandCycleRotationService);
  orchestratorService = inject(StandCycleRotationOrchestratorService);

  service: ICrudService<StandCycleRotationProjectionDto, StandCycleRotationCreateDto, StandCycleRotationUpdateDto, StandCycleRotationProjectionExcelDownloadDto> = {
    create: input => this.geographicService.create(input),
    update: (id, input) => this.orchestratorService.update(id, input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'standCycleRotationId',
    ),
    deleteByIds: ids => this.geographicService.deleteByIds(ids),
    getList: (query: GetStandCycleRotationProjectionListInputDto) =>
      this.orchestratorService.getList(
        normalizePropertyPrefilterQuery(query),
      ),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input =>
      this.orchestratorService.getListAsExcelFile(
        normalizePropertyPrefilterQuery(input),
      ),
  };

  config = STANDCYCLEROTATION_CONFIG;
  fields = STANDCYCLEROTATION_FIELDS;

  actions: IActionsSchema | null = null;

  ngOnInit(): void {
    if (!this.config.actions || !this.config.actionIds) {
      return;
    }

    this.actions = resolveActions(
      this.config.actions,
      {
        [this.config.actionIds.calculateDualVolumeTreeTrigger]:
          () => this.calculateDualVolumeTree(),
      },
    );
  }

  protected override prepareCreateDto(data: any): StandCycleRotationCreateDto {
    return {
      ...data,
      isStumpCandidateToRegrowth: data.isStumpCandidateToRegrowth ?? false,
      isRegrowthCandidate: data.isRegrowthCandidate ?? false,
    };
  }

  protected prepareUpdateDto(
    data: any,
    selected: StandCycleRotationProjectionDto,
  ): StandCycleRotationUpdateDto {
    const fromDataOrSelected = <T>(key: string, fallback: T) =>
      data[key] === undefined ? fallback : data[key];

    return {
      isFscCertified: fromDataOrSelected('isFscCertified', selected.isFscCertified ?? false),
      isPefcCertified: fromDataOrSelected('isPefcCertified', selected.isPefcCertified ?? false),
      isInventoryValidated: data.isInventoryValidated,
      dab: data.dab,
      applyFeller: data.applyFeller,
      nextCycleEstimatedAge: data.nextCycleEstimatedAge,
      dualPercentage: data.dualPercentage,
      dualVolumeTree: fromDataOrSelected('dualVolumeTree', selected.dualVolumeTree ?? 0),
      isStumpCandidateToRegrowth: fromDataOrSelected('isStumpCandidateToRegrowth', selected.isStumpCandidateToRegrowth ?? false),
      isRegrowthCandidate: fromDataOrSelected('isRegrowthCandidate', selected.isRegrowthCandidate ?? false),
      rotationId: data.rotationId,
      propertyTypeId: data.propertyTypeId,
      fomentContractId: data.fomentContractId,
      forestryStatusId: data.forestryStatusId,
      productivityParameterId: data.productivityParameterId,
      stumpApplicationId: data.stumpApplicationId,
      landContractId: data.landContractId,
      measurementTypeId: data.measurementTypeId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'StandCycleRotation';
  }
  private calculateDualVolumeTree(): void {
    const ids = this.selectedRows;

    if (!ids?.length) {
      return;
    }

    this.orchestratorService.calculateDualVolumeTree(ids).subscribe({
      next: () => {
        this.toaster.success('LanguageService::Actions:SuccessMessage', '', {life: 5000, messageLocalizationParams: [this.localizationService.instant('Geographic::Action.Trigger.CalculateDualVolumeTree')] })
      },
      error: (err) => {
        console.error(err);
      }
    });
  }
}
